import React, { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import UserDashboard from 'layouts/UserDashboard';
import Container from 'components/Container';
import { getAchievements, markAchievementSeen } from './FinancialService';
import { UserAchievementResponse } from './types';
import { extractErrorMessage } from '../../../utils/extractErrorMessage';

const ALL_ACHIEVEMENTS = [
  {
    code: 'FIRST_GOAL',
    title: 'Goal Setter',
    description: 'Created your first financial goal.',
    iconKey: 'star',
  },
  {
    code: 'GOAL_COMPLETED',
    title: 'Goal Achiever',
    description: 'Completed your first financial goal.',
    iconKey: 'trophy',
  },
  {
    code: 'PROGRAMME_GRADUATE',
    title: 'Programme Graduate',
    description: 'Completed your first Docket programme.',
    iconKey: 'graduation-cap',
  },
  {
    code: 'SAVINGS_100',
    title: 'First £100 Saved',
    description: 'Saved a total of £100 across your goals.',
    iconKey: 'piggy-bank',
  },
  {
    code: 'SPENDING_STREAK_7',
    title: '7-Day Streak',
    description: 'Stayed within budget for 7 days in a row.',
    iconKey: 'fire',
  },
];

const AchievementsPage = (): JSX.Element => {
  const [unlocked, setUnlocked] = useState<UserAchievementResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getAchievements()
      .then((res) => {
        setUnlocked(res.data);
        // Mark unseen achievements as seen
        res.data
          .filter((a) => !a.seen)
          .forEach((a) => markAchievementSeen(a.id).catch(() => {}));
      })
      .catch((err) => setError(extractErrorMessage(err)))
      .finally(() => setLoading(false));
  }, []);

  const unlockedCodes = new Set(unlocked.map((a) => a.code));
  const unseenCount = unlocked.filter((a) => !a.seen).length;

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });

  return (
    <Container>
      <UserDashboard>
        <Box marginTop={2} marginBottom={4}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Typography variant="h4">Achievements</Typography>
            {unseenCount > 0 && (
              <Chip
                label={`${unseenCount} new`}
                color="warning"
                size="small"
              />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">
            Unlock achievements by reaching financial milestones and completing
            programmes.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {loading
            ? Array.from({ length: 5 }).map((_, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 2 }} />
              </Grid>
            ))
            : ALL_ACHIEVEMENTS.map((a) => {
              const earned = unlockedCodes.has(a.code);
              const ua = unlocked.find((u) => u.code === a.code);

              return (
                <Grid item xs={12} sm={6} md={4} key={a.code}>
                  <Tooltip
                    title={earned ? `Unlocked ${formatDate(ua!.unlockedAt )}` : 'Not yet unlocked'}
                    placement="top"
                  >
                    <Card
                      sx={{
                        height: '100%',
                        opacity: earned ? 1 : 0.45,
                        border: earned ? '2px solid' : '1px solid',
                        borderColor: earned ? 'primary.main' : 'divider',
                        transition: 'opacity 0.2s',
                        position: 'relative',
                        overflow: 'visible',
                      }}
                    >
                      {earned && !ua?.seen && (
                        <Badge
                          badgeContent="New"
                          color="warning"
                          sx={{
                            position: 'absolute',
                            top: -8,
                            right: 12,
                            '& .MuiBadge-badge': { fontSize: 10 },
                          }}
                        />
                      )}
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
                          <Box
                            sx={{
                              width: 44,
                              height: 44,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: earned ? 'primary.main' : 'action.disabledBackground',
                            }}
                          >
                            {earned ? (
                              <EmojiEventsIcon sx={{ color: 'white', fontSize: 22 }} />
                            ) : (
                              <LockOutlinedIcon sx={{ fontSize: 22, color: 'text.disabled' }} />
                            )}
                          </Box>
                          <Typography fontWeight={700}>{a.title}</Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {a.description}
                        </Typography>
                        {earned && ua && (
                          <Chip
                            label={`Earned ${formatDate(ua.unlockedAt )}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ mt: 1.5 }}
                          />
                        )}
                      </CardContent>
                    </Card>
                  </Tooltip>
                </Grid>
              );
            })}
        </Grid>
      </UserDashboard>
    </Container>
  );
};

export default AchievementsPage;
